import React, { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { Grid } from "@mui/material";
import EzDialog from "../../../../common/EzDialog";
import "../style.css";
import AuditTrailSection from "../AuditTrailSection";
import EzControlledInputWithFloatingLabel from "../../../../common/EzForm/EzControlledInput/WithFloatingLabel";
import EzControlledPasswordWithFloatingLabel from "../../../../common/EzForm/EZControlledPassword/WithFloatingLabel";

const NetworkCredentialsDialog = ({
  visible,
  onHide,
  networkId,
  networkCredentials,
  auditTrailList,
  auditTrailPaginationInfo,
  isNetworkCredentialsSuccess,
  isUpdateNetworkCredentialsSuccess,
  isLoading,
  fetchNetworkCredentials,
  updateNetworkCredentials,
  fetchNetworkAuditTrail,
}) => {
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [hasSubmittedCredentials, setHasSubmittedCredentials] = useState(false);

  // Derive safe values
  const auditTrail = auditTrailList || [];
  const auditTrailPagination = auditTrailPaginationInfo || {
    page: 1,
    pageSize: 10,
    totalRecords: 0,
  };

  // Form for credentials
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      authName: "",
      authKey: "",
    },
  });

  // Fetch credentials when dialog opens
  useEffect(() => {
    if (visible && networkId) {
      fetchNetworkCredentials({ networkId });
    }
  }, [visible, networkId, fetchNetworkCredentials]);

  // Populate form when credentials are fetched - Fixed: map snake_case to camelCase
  useEffect(() => {
    if (visible && isNetworkCredentialsSuccess && networkCredentials) {
      reset({
        authName:
          networkCredentials?.auth_name || networkCredentials?.authName || "",
        authKey:
          networkCredentials?.auth_key || networkCredentials?.authKey || "",
      });
    }
  }, [visible, isNetworkCredentialsSuccess, networkCredentials, reset]);

  // Fetch audit trail after successful credential update
  useEffect(() => {
    if (isUpdateNetworkCredentialsSuccess && showAuditTrail && networkId) {
      fetchNetworkAuditTrail({
        networkId,
        page: 1,
        pageSize: 10,
      });
    }
  }, [
    isUpdateNetworkCredentialsSuccess,
    showAuditTrail,
    networkId,
    fetchNetworkAuditTrail,
  ]);

  const handleFetchAuditTrail = useCallback(
    (page = 1) => {
      if (!networkId) return;
      fetchNetworkAuditTrail({
        networkId,
        page,
        pageSize: 10,
      });
    },
    [fetchNetworkAuditTrail, networkId],
  );

  const handleToggleAuditTrail = () => {
    const nextState = !showAuditTrail;
    setShowAuditTrail(nextState);
    if (nextState) {
      handleFetchAuditTrail(1);
    }
  };

  const onCredentialsSubmit = (data) => {
    setHasSubmittedCredentials(true);
    updateNetworkCredentials({
      networkId: networkId,
      authName: data.authName,
      authKey: data.authKey,
      userId: localStorage.getItem("userId"),
    });
  };

  const handleDialogHide = () => {
    setShowAuditTrail(false);
    setHasSubmittedCredentials(false);
    reset({
      authName: "",
      authKey: "",
    });
    onHide();
  };

  useEffect(() => {
    if (visible && hasSubmittedCredentials && isUpdateNetworkCredentialsSuccess) {
      handleDialogHide();
    }
  }, [visible, hasSubmittedCredentials, isUpdateNetworkCredentialsSuccess]);

  return (
    <EzDialog
      onHide={handleDialogHide}
      visible={visible}
      header="Update network credential"
      width="700px"
      height="auto"
      maximizable={false}
      className="network-credentials-dialog"
    >
      <form onSubmit={handleSubmit(onCredentialsSubmit)} className="p-fluid">
        <div className="credentials-dialog-content">
          <div className="credentials-form-section">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className="field-wrapper">
                  <EzControlledInputWithFloatingLabel
                    name="authName"
                    control={control}
                    label="Authentication Name"
                    type="text"
                    rules={{
                      required: "Authentication Name is required",
                    }}
                    isError={errors?.authName}
                    errorMsg={errors?.authName?.message}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="field-wrapper">
                  <EzControlledPasswordWithFloatingLabel
                    name="authKey"
                    control={control}
                    label="Auth Key"
                    rules={{ required: "Auth Key is required" }}
                    isError={errors?.authKey}
                    errorMsg={errors?.authKey?.message}
                  />
                </div>
              </Grid>
            </Grid>
          </div>

          {/* Audit Trail Section */}
          <AuditTrailSection
            showAuditTrail={showAuditTrail}
            onToggle={handleToggleAuditTrail}
            auditTrail={auditTrail}
            auditTrailPagination={auditTrailPagination}
            onPageChange={handleFetchAuditTrail}
            isLoading={isLoading}
          />

          <div className="credentials-dialog-actions">
            <Button
              type="button"
              label="Cancel"
              className="p-button-secondary p-button-sm"
              onClick={handleDialogHide}
              disabled={isLoading}
            />

            <Button
              type="submit"
              label="Update"
              className="p-button-sm"
              loading={isLoading}
            />
          </div>
        </div>
      </form>
    </EzDialog>
  );
};

export default NetworkCredentialsDialog;
